import type { RoosterMatch } from '@/types/rooster-match.type'

import { useMemo } from 'react'
import { fDate, fTime } from '@/utils/format-date'
import { Separator } from '@/components/ui/separator'
import {
  MapPin,
  User,
  Hash,
  Phone,
  Banknote,
  Weight,
  Award,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

import { getStatusBadge } from './helper'

// Helper function to format rooster type
const formatRoosterType = (type: string) => {
  const typeMap: Record<string, string> = {
    THAI: 'ไก่ชนไทย',
    BURMESE: 'ไก่ชนพม่า',
    VIETNAMESE: 'ไก่ชนเวียดนาม',
    MIXED: 'ไก่ชนผสม',
    OTHER: 'อื่นๆ',
  }
  return typeMap[type] || type
}

interface RoosterMatchDetailDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  match: RoosterMatch | null
}

export default function RoosterMatchDetailDialog({
  open,
  onOpenChange,
  match,
}: RoosterMatchDetailDialogProps) {
  const basicInfo = useMemo(
    () =>
      match
        ? [
            { label: 'รหัสการแข่งขัน', value: match.matchNumber || '-' },
            { label: 'สถานะ', value: getStatusBadge(match.status) },
            { label: 'วันที่แข่งขัน', value: fDate(match.matchDate) },
            { label: 'เวลาการแข่งขัน', value: fTime(match.matchDate) },
          ]
        : [],
    [match],
  )

  const roosterInfo = useMemo(
    () =>
      match
        ? [
            {
              label: 'ไก่เจ้าบ้าน',
              value: match.rooster1?.name || '-',
              owner: match.ownerRooster1?.displayName || '-',
              contact: match.rooster1?.ownerContactNumber || '-',
              weight: match.rooster1?.weight || '-',
              type: match.rooster1?.type || '-',
              imageUrls: match.rooster1?.imageUrls || [],
            },
            {
              label: 'ไก่แขก',
              value: match.rooster2?.name || 'รอคู่แข่ง',
              owner: match.ownerRooster2?.displayName || '-',
              contact: match.rooster2?.ownerContactNumber || '-',
              weight: match.rooster2?.weight || '-',
              type: match.rooster2?.type || '-',
              imageUrls: match.rooster2?.imageUrls || [],
            },
          ]
        : [],
    [match],
  )

  const formatBetAmount = (amount: number | null | undefined) => {
    if (!amount) return '- บาท'

    // Format large numbers more compactly
    if (amount >= 1e12) {
      return `${(amount / 1e12).toFixed(1)} ล้านล้าน บาท`
    } else if (amount >= 1e9) {
      return `${(amount / 1e9).toFixed(1)} พันล้าน บาท`
    } else if (amount >= 1e6) {
      return `${(amount / 1e6).toFixed(1)} ล้าน บาท`
    } else if (amount >= 1e3) {
      return `${(amount / 1e3).toFixed(1)} พัน บาท`
    } else {
      return `${amount.toLocaleString()} บาท`
    }
  }

  const betInfo = useMemo(
    () =>
      match
        ? [
            {
              label: 'เดิมพันต่ำสุด',
              value: formatBetAmount(match.minBetAmount),
            },
            {
              label: 'เดิมพันสูงสุด',
              value: formatBetAmount(match.maxBetAmount),
            },
            {
              label: 'เดิมพันจริง',
              value: formatBetAmount(match.actualBetAmount),
            },
          ]
        : [],
    [match],
  )

  const systemInfo = useMemo(
    () =>
      match
        ? [
            { label: 'วันที่สร้าง', value: fDate(match.createdAt) },
            { label: 'อัปเดตล่าสุด', value: fDate(match.updatedAt) },
          ]
        : [],
    [match],
  )

  const renderInfoSection = (
    title: string,
    items: Array<{ label: string; value: string | React.ReactNode }>,
  ) => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">{title}</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
        {items.map((item, index) => (
          <div key={item.label || index}>
            <div className="text-muted-foreground mb-1">{item.label}</div>
            <div className="font-medium truncate max-w-[300px]">
              {item.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  const renderRoosterSection = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">ข้อมูลการแข่งขัน</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {roosterInfo.map((rooster, index) => (
          <div
            key={index}
            className="space-y-4 p-4 bg-muted/20 rounded-lg border"
          >
            {/* Rooster Header */}
            <div className="flex items-center gap-2">
              <Hash className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium text-sm text-muted-foreground">
                {rooster.label}
              </span>
            </div>

            {/* Rooster Name */}
            <div className="pl-6">
              <div className="text-lg font-semibold text-primary">
                {rooster.value}
              </div>
            </div>

            {/* Rooster Details */}
            <div className="space-y-3">
              <div className="grid grid-cols-1 gap-3 text-sm">
                <div className="flex items-center gap-2">
                  <Weight className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">น้ำหนัก:</span>
                  <span className="font-medium">
                    {typeof rooster.weight === 'number'
                      ? `${(rooster.weight / 1000).toFixed(2)} กก.`
                      : rooster.weight}
                  </span>
                </div>

                <div className="flex items-center gap-2">
                  <Award className="h-3 w-3 text-muted-foreground" />
                  <span className="text-muted-foreground">ประเภท:</span>
                  <span className="font-medium">
                    {formatRoosterType(rooster.type)}
                  </span>
                </div>
              </div>

              {/* Rooster Images */}
              {rooster.imageUrls && rooster.imageUrls.length > 0 && (
                <div className="space-y-2">
                  <div className="text-sm text-muted-foreground">รูปภาพ:</div>
                  <div className="flex gap-2 flex-wrap">
                    {rooster.imageUrls.slice(0, 3).map((url, imgIndex) => (
                      <div
                        key={imgIndex}
                        className="w-16 h-16 rounded-lg overflow-hidden border"
                      >
                        <img
                          src={url}
                          alt={`${rooster.value} ${imgIndex + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ))}
                    {rooster.imageUrls.length > 3 && (
                      <div className="w-16 h-16 rounded-lg border-2 border-dashed border-muted-foreground/50 flex items-center justify-center text-xs text-muted-foreground">
                        +{rooster.imageUrls.length - 3}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Owner Information */}
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <User className="h-3 w-3" />
                <span>เจ้าของ: {rooster.owner}</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Phone className="h-3 w-3" />
                <span>เบอร์ติดต่อ: {rooster.contact}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  const renderBetSection = () => (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold">ข้อมูลเดิมพัน</h3>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {betInfo.map((bet, index) => (
          <div key={index} className="space-y-3 p-4 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-2">
              <Banknote className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium text-muted-foreground">
                {bet.label}
              </span>
            </div>
            <div className="text-lg font-semibold text-center break-words">
              {bet.value}
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  const renderArenaSection = () => {
    if (!match?.arena) return null

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">สนามแข่งขัน</h3>
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-muted-foreground" />
          <span className="text-base font-medium">{match.arena.name}</span>
        </div>
        {match.arena.contactNumber && (
          <div className="text-sm text-muted-foreground">
            เบอร์ติดต่อ: {match.arena.contactNumber}
          </div>
        )}
      </div>
    )
  }

  const renderNoteSection = () => {
    if (!match?.note) return null

    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold">หมายเหตุ</h3>
        <div className="text-sm bg-muted/50 p-4 rounded-lg">{match.note}</div>
      </div>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>รายละเอียดการแข่งขัน</DialogTitle>
          <DialogDescription>
            แสดงรายละเอียดทั้งหมดของการแข่งขันไก่ชน
          </DialogDescription>
        </DialogHeader>

        {match && (
          <div className="space-y-6">
            {renderInfoSection('ข้อมูลพื้นฐาน', basicInfo)}
            <Separator />
            {renderRoosterSection()}
            <Separator />
            {renderBetSection()}
            <Separator />
            {renderArenaSection()}
            {match.note && (
              <>
                <Separator />
                {renderNoteSection()}
              </>
            )}
            <Separator />
            {renderInfoSection('ข้อมูลระบบ', systemInfo)}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
